import { Routes } from '@angular/router';
import { FullComponent } from './layouts/full/full.component';
import { PartialComponent } from './layouts/partial/partial.component';
import { AuthCallbackComponent, AuthLoggedOutComponent } from './components';
import {
  ApproveNewUserComponent,
  ArtifactListComponent,
  BannerTestComponent,
  CachedHomesComponent,
  CompaniesAddEditComponent,
  CompaniesListComponent,
  CompanyComponent,
  CompanyReportSubscriptionsComponent,
  CredentialsComponent,
  GettingStartedComponent,
  HomeAddComponent,
  HomeDetailComponent,
  HomeListComponent,
  HomeViewerComponent,
  IfcReportComponent,
  LandingPageComponent,
  LegalAgreementComponent,
  PartnershipComponent,
  PartnershipRequestComponent,
  PartnershipsListComponent,
  PdfViewerTestComponent,
  ProductImportPreviewComponent,
  ProductImpressionsComponent,
  ProductImpressionsGraphComponent,
  MonthlyBillingSummaryComponent,
  ProductDetailsReportComponent,
  ThemeProductProgressReportComponent,
  ProductsAddEditComponent,
  NewProductEditComponent,
  RequestedProductsListComponent,
  ProductCategorization,
  ProductReplacementTags,
  ProductSubstitutionsComponent,
  ProductsListComponent,
  RendererListComponent,
  UnauthorizedComponent,
  UserDetailComponent,
  UserDetailsComponent,
  UserListComponent,
  VerifyEmailComponent,
  ViewHomeAnalyticsComponent,
  WarpProductEnvironmentsComponent,
  AddressTestComponent,
  SituApiManageClientsComponent,
  SituApiAddClientComponent,
  RangesListComponent,
  RangesAddEditComponent,
  InteractiveThemeEditor,
  ThemesListComponent,
  IfcRoomsConfigComponent,
  IfcBuildingMaterialsConfigComponent,
  IfcStoreysConfigComponent,
  IfcFurnishingsConfigComponent,
  SituFacadeComponent,
  SituViewComponent,
  SituBrowseComponent,
  RenderedOutputListComponent,
  RecipeEventSummaryListComponent,
  RecipeEventSummaryComponent,
  StyleTemplateListComponent,
  StyleTemplateEditComponent,
  AccessoryBundleListComponent,
  AccessoryBundleEditComponent,
  AccessoryBundleAddComponent,
  PlatformUsageComponent,
  ReleaseListComponent,
  ReleaseOverviewComponent,
  ReleaseEditComponent,
  ThemesImportedProductReplacementsComponent,
  ThemesDetailComponent,
  ProductCatalogComponent,
  ThemesAddComponent,
  ThemesCopyComponent,
  HomeDetailsListComponent,
  PlaceholderTemplatesComponent,
  BuilderOptionsComponent,
  RequestedSupplierListComponent,
  ReleaseIssuesReportComponent,
  TagListComponent,
  ProductDiscoverComponent,
  FileVaultBrowserComponent,
  SituReelsListComponent
} from './views';
import { Role } from './shared/models/role.model';
import { AuthGuard } from './shared/guards/auth.guard';
import { EnvironmentsListComponent } from './views/environments/environments-list/environments-list.component';
import { OverridesAddEditComponent } from './views/environments/overrides-add-edit/overrides-add-edit.component';
import { RendererDashboardComponent } from './views/admin/renderers/renderer-dashboard/renderer-dashboard.component';
import { QueueListComponent } from './views/admin/renderers/queue-list/queue-list.component';
import { RendererPoolListComponent } from './views/admin/renderers/renderer-pool-list/renderer-pool-list.component';
import { StyleTreeComponent } from './views/admin/styles/style-tree/style-tree.component';
import { SituViewReadonlyComponent } from './views/render-product/situ-view-readonly/situ-view-readonly.component';
import { SituReadonlyFactoryComponent } from './views/render-product/situ-readonly-factory/situ-readonly-factory.component';
import { FacadeReportComponent } from './views/reports/facade-report/facade-report.component';
import { RenderSummaryReportComponent } from './views/reports/render-summary-report/render-summary-report.component';
import { BuilderRepresentationReportComponent } from './views/reports/builder-representation-report/builder-representation-report.component';
import { SupplierDashboardComponent } from './views/reports/supplier-dashboard/supplier-dashboard.component';
import { ThemesImportedLinesComponent } from './views/themes/themes-imported-lines/themes-imported-lines.component';
import { SituConnectComponent } from './views/render-product/situ-connect/situ-connect.component';
import { BrandsComponent } from './views/products/brands/brands.component';
import { MarketingComponent } from './views/products/marketing/marketing.component';
import { TileContentComponent } from './views/products/tile-content/tile-content.component';

export const AppRoutes: Routes = [
  {
    path: '',
    component: FullComponent,
    children: [
      { path: '', redirectTo: 'homes/dashboard', pathMatch: 'full' },
      { path: 'auth-callback', component: AuthCallbackComponent },
      { path: 'auth-logged-out', component: AuthLoggedOutComponent },
      { path: 'unauthorized', component: UnauthorizedComponent },
      {
        path: 'docs/situview', component: GettingStartedComponent, data: {
          external: true,
          title: 'Getting Started',
          urls: [
            { title: 'Docs' },
            { title: 'SituView' },
            { title: 'Getting Started' },
          ]
        }
      },
      {
        path: 'admin/situapi/manage-clients', component: SituApiManageClientsComponent, data: {
          external: true,
          title: 'Manage Clients',
          urls: [
            { title: 'Docs' },
            { title: 'situAPI' },
            { title: 'Clients' },
          ]
        }
      },
      {
        path: 'docs/situapi/add-client', component: SituApiAddClientComponent, data: {
          external: true,
          title: 'Add Client',
          urls: [
            { title: 'Docs' },
            { title: 'situAPI' },
            { title: 'Clients', url: 'docs/situapi/manage-clients' },
            { title: 'Add Client' }
          ]
        }
      },
      { path: 'address-test', redirectTo: 'devs/address-test', pathMatch: 'full' },
      {
        path: 'devs/address-test', component: AddressTestComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsDeveloper],
          external: true,
          title: 'Address Test',
          urls: [
            { title: 'Devs' },
            { title: 'Address Test' },
          ]
        }
      },
      { path: 'pdf-viewer-test', redirectTo: 'devs/pdf-viewer-test', pathMatch: 'full' },
      {
        path: 'devs/pdf-viewer-test', component: PdfViewerTestComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsDeveloper],
          external: true,
          title: 'PDF Viewer Test',
          urls: [
            { title: 'Devs' },
            { title: 'PDF Viewer Test' },
          ]
        }
      },
      { path: 'banner-test', redirectTo: 'devs/banner-test', pathMatch: 'full' },
      {
        path: 'devs/banner-test', component: BannerTestComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsDeveloper],
          external: true,
          title: 'PDF Viewer Test',
          urls: [
            { title: 'Devs' },
            { title: 'PDF Viewer Test' }
          ]
        }
      },
      {
        path: 'legal-agreement', component: LegalAgreementComponent, canActivate: [AuthGuard], data: {
          roles: [Role.Admin, Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
          external: true,
          title: 'Legal Agreement',
          urls: [
            { title: 'Legal Agreement' },
          ]
        }
      },
      {
        path: 'style-guide', redirectTo: 'devs/style-guide', pathMatch: 'full'
      },
      {
        path: 'devs/style-guide', canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsDeveloper],
          external: true,
          title: 'Style Guide',
          urls: [
            { title: 'Devs' },
            { title: 'Style Guide' },
          ]
        },
        loadChildren: () => import('./modules/style-guide/style-guide.module').then(m => m.StyleGuideModule)
      },
      // At least authenticated from here on...
      {
        path: 'environments', component: WarpProductEnvironmentsComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper],
          title: 'Environments :productName',
          urls: [
            { title: 'Environments', url: 'environments' },
          ]
        }
      },
      {
        path: 'environments/:product', component: WarpProductEnvironmentsComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper],
          title: 'Environments',
          urls: [
            { title: 'Environments', url: 'environments' },
            { title: ':productName' }
          ]
        }
      },
      { path: 'reports/homes', redirectTo: 'homes', pathMatch: 'full' },

      // homes
      { path: 'homes', redirectTo: 'homes/dashboard', pathMatch: 'full' },
      { path: 'homes/dashboard', component: HomeListComponent, canActivate: [AuthGuard], data: {  title: 'Homes' } },
      { path: 'homes/situview', redirectTo: 'situ-view' },
      { path: 'homes/situfacade', redirectTo: 'situ-facade' },
      { path: 'demo', redirectTo: 'panorama-demo' },
      { path: 'homes/demo', redirectTo: 'panorama-demo' },
      { path: 'panorama-demo', component: HomeViewerComponent, canActivate: [AuthGuard], data: { title: 'Panorama Demo', roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee] } },
      { path: 'homes/details', component: HomeDetailsListComponent, canActivate: [AuthGuard], data: {  title: 'Homes' } },
      {
        path: 'homes/placeholder-templates', component: PlaceholderTemplatesComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          title: 'Placeholder Templates',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Placeholder Templates' }
          ]
        }
      },
      {
        path: 'homes/options', component: BuilderOptionsComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          title: 'Options',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Options' }
          ]
        }
      },
      {
        path: 'homes/upload', component: HomeAddComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.PartnerAdmin, Role.Admin],
          title: ':home',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: ':home' }
          ]
        }
      },
      // SituFurnish
      {
        path: 'homes/furnish', canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.SituFurnish],
          title: 'situFURNISH',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Manage' }
          ]
        },
        loadChildren: () => import('./modules/situ-furnish/situ-furnish.module').then(m => m.SituFurnishModule)
      },
      // SituLandscape
      {
        path: 'homes/landscape', canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.SituLandscape],
          title: 'situSCAPE',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Manage' }
          ]
        },
        loadChildren: () => import('./modules/situ-landscape/situ-landscape.module').then(m => m.SituLandscapeModule)
      },
      // Themes
      {
        path: 'homes/themes', component: ThemesListComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Theme],
          title: 'Themes',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Themes' }
          ]
        }
      },
      {
        path: 'homes/themes/add', component: ThemesAddComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Theme],
          title: 'Create new Theme',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Themes', url: '/homes/themes' },
            { title: 'Add' }
          ]
        }
      },
      {
        path: 'homes/themes/copy', component: ThemesCopyComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Theme],
          title: 'Copy Theme',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Themes', url: '/homes/themes' },
            { title: 'Copy' }
          ]
        }
      },
      {
        path: 'homes/themes/theme/:themeId', component: ThemesDetailComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Theme],
          title: ':name',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Themes', url: '/homes/themes' },
            { title: 'Detail' }
          ]
        }
      },
      {
        path: 'homes/themes/:companyId/:themeId', component: ThemesDetailComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Theme],
          title: ':name',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: 'Themes', url: '/homes/themes' },
            { title: 'Detail' }
          ]
        }
      },
      {
        path: 'homes/themes/imported-lines', component: ThemesImportedLinesComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Theme],
          
          title: 'Themes',
          urls: [
            { title: 'Themes' }
          ]
        }
      },
      {
        path: 'homes/themes/imported-product-replacements', component: ThemesImportedProductReplacementsComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Theme],
          
          title: 'Themes',
          urls: [
            { title: 'Themes' },
            { title: 'Imported', url: '/homes/themes/imported-lines' },
          ]
        }
      },
      {
        path: 'homes/:home', component: HomeDetailComponent, canActivate: [AuthGuard], data: {
          
          title: ':home',
          urls: [
            { title: 'Homes', url: '/homes/dashboard' },
            { title: ':home' }
          ]
        }
      },

      // products
      { path: 'products', redirectTo: 'products/dashboard', pathMatch: 'full' },
        {
          path: 'products/dashboard', component: ProductsListComponent, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User],
            title: 'Products',
            urls: [
              { title: 'Products' }
            ]
          }
        },
        {
          path: 'products/catalog', component: ProductCatalogComponent, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User],
            title: 'Catalog',
            urls: [
              { title: 'Catalog' },
            ]
          }
        },
        {
          path: 'products/discover', component: ProductDiscoverComponent, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User],
            title: 'Discover Product Usage',
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Discover' },
            ]
          }
        },
        {
          path: 'products/categorization', component: ProductCategorization, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
            title: 'Product Categorization',
            urls: [
              { title: 'Categorization' },
            ]
          }
        },
        {
          path: 'products/replacement-tags', component: ProductReplacementTags, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
            title: 'Product Replacement Tags',
            urls: [
              { title: 'Replacement Tags' },
            ]
          }
        },
        {
          path: 'products/substitutions', component: ProductSubstitutionsComponent, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
            title: 'Product Substitutions',
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Product Substitutions' }
            ]
          }
        },
        {
          path: 'products/brands', component: BrandsComponent, canActivate: [AuthGuard],
          data: {
            title: 'Product Brands',
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.PartnerAdmin, Role.Admin],
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Product Brands' },
            ]
          }
        },
        {
          path: 'products/content', component: TileContentComponent, canActivate: [AuthGuard],
          data: {
            title: 'situREACH Content',
            // Situ staff only while the page is proven out. The role and
            // ownership checks for Admin / Builder Admin / Supplier Marketing /
            // Builder Marketing are in place; widen this list to open it to them.
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'situREACH Content' },
            ]
          }
        },
        {
          path: 'products/marketing', component: MarketingComponent, canActivate: [AuthGuard],
          data: {
            title: 'Product Marketing',
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.PartnerAdmin, Role.Admin, Role.BuilderAdmin],
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Product Marketing' },
            ]
          }
        },
        {
          path: 'products/ranges', component: RangesListComponent, canActivate: [AuthGuard],
          data: {
            title: 'Product Ranges',
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.PartnerAdmin, Role.Admin],
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Product Ranges' },
            ]
          }
        },
        {
          path: 'products/product/add', component: ProductsAddEditComponent, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
            
            title: 'Add Legacy Product',
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Add Legacy Product :productSku' }
            ]
          }
        },
        {
          path: 'products/product/:productId', component: ProductsAddEditComponent, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
            
            title: 'Legacy Product',
            urls: [
              { title: 'Legacy Products', url: '/products/dashboard' },
              { title: ':productSku' }
            ]
          }
        },
        {
          path: 'products/ranges/add', component: RangesAddEditComponent, canActivate: [AuthGuard],
          data: {
            title: 'Add Range',
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.PartnerAdmin, Role.Admin],
            
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Product Ranges', url: '/products/ranges' },
              { title: 'Add Range' }
            ]
          }
        },
        {
          path: 'products/ranges/:id', component: RangesAddEditComponent, canActivate: [AuthGuard],
          data: {
            title: ':rangeName',
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.PartnerAdmin, Role.Admin],
            
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Product Ranges', url: '/products/ranges' },
              { title: ':rangeName' }
            ]
          }
        },
        {
          path: 'products/add', component: NewProductEditComponent, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.BuilderAdmin],
            title: 'Add Product',
            urls: [
              { title: 'Catalog', url: '/products/catalog' },
              { title: 'Add Product' }
            ]
          }
        },
        {
          path: 'products/requested-products', component: RequestedProductsListComponent, canActivate: [AuthGuard], data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.BuilderAdmin, Role.CompanyProductPublisher],
            title: 'Requested Products', urls: [
              { title: 'Requested Products' }
            ]
          }
        },
        // SituDecorate
        {
          path: 'products/decorate', canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Theme],
            title: 'situDECORATE',
            urls: [
              { title: 'Products', url: '/products/dashboard' },
              { title: 'Decorate' }
            ]
          },
          loadChildren: () => import('./modules/situ-furnish/situ-decorate.module').then(m => m.SituDecorateModule)
        },
        {
          path: 'products/:productId', component: NewProductEditComponent, canActivate: [AuthGuard],
          data: {
            roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.BuilderAdmin, Role.ExternalProductContractor, Role.CompanyProductPublisher],
            title: 'Product',
            urls: [
              { title: 'Catalog', url: '/products/catalog' },
              { title: ':productSku' }
            ]
          }
        },
      {
        path: 'products-import-preview', component: ProductImportPreviewComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.BuilderAdmin],
          urls: [
            { title: 'Products', url: '/products/dashboard' },
            { title: 'Product Import Preview' },
          ]
        }
      },

      // situFACADE
      {
        path: 'situ-facade', component: SituFacadeComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.SituFacade ],
          title: ':home',
          urls: [
            { title: 'Browse', url: '/situ-facade/browse/5' },
            { title: 'Create' }
          ],
          videos: [
            { url: 'http://cdn.situsystems.com/learn/situfacade/SituFacade01_Last10panel_250207_155200.mp4', title: 'situFACADE - Last 10 Panel' },
            { url: 'http://cdn.situsystems.com/learn/situfacade/SituFacade02_Downloads_250207_160100.mp4', title: 'situFACADE - Downloads' },
            { url: 'http://cdn.situsystems.com/learn/situfacade/SituFacade03_HomeConfiguration_250214_095000.mp4', title: 'situFACADE - Home Configuration' },
            { url: 'http://cdn.situsystems.com/learn/situfacade/SituFacade04_StyleSelections_250207_162200.mp4', title: 'situFACADE - Style Selections' },
            { url: 'http://cdn.situsystems.com/learn/situfacade/SituFacade05_ConfigurationGallery_250213_095700.mp4', title: 'situFACADE - Configuration Gallery' }
          ]
        }
      },
      {
        path: 'situ-facade/browse/:product', component: SituBrowseComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User ],
          title: 'Browse Catalog',
          urls: [
            { title: 'Browse Catalog' }
          ]
        }
      },
      {
        path: 'situ-facade/browse', component: SituBrowseComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User ],
          title: 'Browse Catalog',
          urls: [
            { title: 'Browse Catalog' }
          ]
        }
      },

      // situVIEW
      {
        path: 'situ-view', component: SituViewComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.SituView ],
          title: ':home',
          urls: [
            { title: 'Browse', url: '/situ-view/browse/0' },
            { title: 'Create' }
          ]
        }
      },
      {
        path: 'situ-view/browse/:product', component: SituBrowseComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User ],
          
          title: 'Browse Catalog',
          urls: [
            { title: 'Browse Catalog' }
          ]
        }
      },
      {
        path: 'situ-view/browse', component: SituBrowseComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User ],
          title: 'Browse Catalog',
          urls: [
            { title: 'Browse Catalog' }
          ]
        }
      },

      // situDAYDREAM
      {
        path: 'situ-daydream', canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.SituDaydream ],
          title: 'situDAYDREAM',
          urls: [
            { title: 'situFACADE', url: '/situ-facade' },
            { title: 'situDAYDREAM' }
          ]
        },
        loadChildren: () => import('./modules/situ-daydream/situ-daydream.module').then(m => m.SituDaydreamModule)
      },

      // situVanity
      {
        path: 'situ-vanity', canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.SituVanity ],
          external: true,
          title: 'situVANITY',
          urls: [
            { title: 'situVANITY' },
          ]
        },
        loadChildren: () => import('./modules/situ-vanity/situ-vanity.module').then(m => m.SituVanityModule)
      },

      // situCONNECT
      {
        path: 'situ-connect', component: SituConnectComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.SituConnect ],
          title: 'Connect',
          urls: [
            { title: 'Browse', url: '/situ-connect/browse' },
            { title: 'Connect' }
          ]
        }
      },
      {
        path: 'situ-connect/browse', component: SituBrowseComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.SituConnect ],
          title: 'Browse Catalog',
          urls: [
            { title: 'Browse Catalog' }
          ]
        }
      },
      {
        path: 'situ-facade/browse/:product', component: SituBrowseComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User ],
          
          title: 'Browse Catalog',
          urls: [
            { title: 'Browse Catalog' }
          ]
        }
      },
      {
        path: 'situ-facade/browse', component: SituBrowseComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.User ],
          
          title: 'Browse Catalog',
          urls: [
            { title: 'Browse Catalog' }
          ]
        }
      },

      // reports
      { path: 'reports/homes', redirectTo: 'homes', pathMatch: 'full' },
      { path: 'reports/homes-old', redirectTo: 'homes-old', pathMatch: 'full' },
      {
        path: 'reports/cached-homes', component: CachedHomesComponent, canActivate: [AuthGuard], data: {
          
          title: 'Cached Homes',
          urls: [
            { title: 'Reports' },
            { title: 'Cached Homes' }
          ]
        }
      },
      {
        path: 'reports/ifc-report', component: IfcReportComponent, canActivate: [AuthGuard], data: {
          
          title: 'IFC File Feed',
          urls: [
            { title: 'Reports' },
            { title: 'IFC File Feed' }
          ]
        }
      },
      {
        path: 'reports/pre-cache-homes', redirectTo: 'admin/pre-cache-homes', pathMatch: 'full'
      },
      {
        path: 'reports/platform-usage', component: PlatformUsageComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
          title: 'Platform Usage',
          hideSidebar: true,
          urls: [
            { title: 'Reports' },
            { title: 'Platform Usage' }
          ]
        }
      },
      {
        path: 'reports/priority-homes', redirectTo: 'admin/priority-homes', pathMatch: 'full'
      },
      {
        path: 'reports/product-impressions-data', component: ProductImpressionsComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin],
          title: 'Product Impressions Data', urls: [
            { title: 'Reports' },
            { title: 'Product Impressions Data' }
          ]
        }
      },
      {
        path: 'reports/product-impressions-graph', component: ProductImpressionsGraphComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin],
          title: 'Product Impressions Graph',
          hideSidebar: true,
          urls: [
            { title: 'Reports' },
            { title: 'Product Impressions Graph' }
          ]
        }
      },
      {
        path: 'reports/monthly-billing-summary', component: MonthlyBillingSummaryComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper],
          title: 'Monthly Billing Summary',
          hideSidebar: true,
          urls: [
            { title: 'Reports' },
            { title: 'Monthly Billing Summary' }
          ]
        }
      },
      {
        path: 'reports/render-summary-report', component: RenderSummaryReportComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper],
          title: 'Render Summary Report',
          hideSidebar: true,
          urls: [
            { title: 'Reports' },
            { title: 'Render Summary Report' }
          ]
        }
      },
      {
        path: 'reports/builder-representation-report', component: BuilderRepresentationReportComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper],
          title: 'Builder Representation Report',
          hideSidebar: true,
          urls: [
            { title: 'Reports' },
            { title: 'Builder Representation Report' }
          ]
        }
      },
      {
        // Mockup only (sample data); Situ staff until the real supplier view exists.
        path: 'reports/supplier-dashboard', component: SupplierDashboardComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper],
          title: 'Supplier Dashboard',
          urls: [
            { title: 'Reports' },
            { title: 'Supplier Dashboard' }
          ]
        }
      },
      {
        path: 'reports/product-details', component: ProductDetailsReportComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
          title: 'Product Details Report', urls: [
            { title: 'Reports' },
            { title: 'Product Details' }
          ]
        }
      },
      // {
      //   path: 'reports/release-issues', component: ReleaseIssuesReportComponent, canActivate: [AuthGuard], data: {
      //     roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
      //     title: 'Release Issues',
      //     urls: [
      //       { title: 'Reports' },
      //       { title: 'Release Issues' }
      //     ]
      //   }
      // },
      {
        path: 'reports/theme-product-progress', component: ThemeProductProgressReportComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
          title: 'Theme and Product Progress Report', urls: [
            { title: 'Reports' },
            { title: 'Theme and Product Progress' }
          ]
        }
      },
      {
        path: 'reports/view-home-analytics', component: ViewHomeAnalyticsComponent, canActivate: [AuthGuard], data: {
          
          title: 'Usage Report', urls: [
            { title: 'Reports' },
            { title: 'Usage Report' }
          ]
        }
      },
      {
        path: 'reports/facade-report', component: FacadeReportComponent, canActivate: [AuthGuard], data: {
          
          title: 'Facade Report', urls: [
            { title: 'Reports' },
            { title: 'Facade Report' }
          ]
        }
      },

      //admin
      {
        path: 'admin/artifacts', component: ArtifactListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsDeveloper], title: 'Artifacts',
          urls: [
            { title: 'Admin' },
            { title: 'Artifacts' }
          ]
        }
      },
      {
        path: 'admin/rendered-output', component: RenderedOutputListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Rendered Output',
          urls: [
            { title: 'Admin' },
            { title: 'Rendered Output' }
          ]
        }
      },
      {
        path: 'admin/situ-reels', component: SituReelsListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin], title: 'SituReels',
          urls: [
            { title: 'Admin' },
            { title: 'SituReels' }
          ]
        }
      },
      {
        path: 'admin/release', component: ReleaseListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Release],
          title: 'Releases',
          urls: [
            { title: 'Admin' },
            { title: 'Releases' }
          ]
        }
      },
      {
        path: 'admin/release-issues', component: ReleaseIssuesReportComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.Release],
          //roles: [Role.SituSystemsAdmin],
          title: 'Release Issues at a glance (:release)',
          hideSidebar: true,
          urls: [
            { title: 'Admin' },
            { title: 'Releases' },
            { title: 'Issues' }
          ]
        }
      },
      {
        path: 'admin/release/:id', component: ReleaseEditComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Release], title: 'Release',
          urls: [
            { title: 'Admin' },
            { title: 'Releases', url: '/admin/release' },
            { title: ':releaseName' }
          ]
        }
      },
      {
        path: 'admin/recipe-event-summary', component: RecipeEventSummaryListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Recipe Event Summaries',
          urls: [
            { title: 'Admin' },
            { title: 'Summaries' }
          ]
        }
      },
      {
        path: 'admin/recipe-event-summary/:id', component: RecipeEventSummaryComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Recipe Event Summary',
          urls: [
            { title: 'Admin' },
            { title: 'Summaries', url: '/admin/recipe-event-summary' },
            { title: 'Summary' }
          ]
        }
      },
      {
        path: 'admin/renderers', component: RendererListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Renderers',
          urls: [
            { title: 'Admin' },
            { title: 'Renderers' }
          ]
        }
      },
      {
        path: 'admin/renderers/:rendererId/dashboard', component: RendererDashboardComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
          title: ':rendererName',
          urls: [
            { title: 'Admin' },
            { title: 'Renderers', url: '/admin/renderers' },
            { title: ':rendererName' }
          ]
        }
      },
      {
        path: 'admin/queues', component: QueueListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Queues',
          urls: [
            { title: 'Admin' },
            { title: 'Queues' }
          ]
        }
      },
      {
        path: 'admin/queues/:queueId/dashboard', component: RendererDashboardComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
          title: ':queueName',
          urls: [
            { title: 'Admin' },
            { title: 'Queues', url: '/admin/queues' },
            { title: ':queueName' }
          ]
        }
      },
      {
        path: 'admin/renderer-pools', component: RendererPoolListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper], title: 'Renderer Pools',
          urls: [
            { title: 'Admin' },
            { title: 'Renderer Pools' }
          ]
        }
      },

      {
        path: 'admin/user', component: UserListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          urls: [
            { title: 'Admin' },
            { title: 'Users' }
          ]
        }
      },
      {
        path: 'admin/userreload/:date', component: UserListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          urls: [
            { title: 'Admin' },
            { title: 'Users' }
          ]
        }
      },
      {
        path: 'admin/user/:userId', component: UserDetailComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          urls: [
            { title: 'Admin' },
            { title: 'Users', url: '/admin/user' },
            { title: ':email' }
          ]
        }
      },
      {
        path: 'admin/create-user', component: UserDetailComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          urls: [
            { title: 'Admin' },
            { title: 'Users', url: '/admin/user' },
            { title: 'Create User' }
          ]
        }
      },
      {
        path: 'admin/partnerships', component: PartnershipsListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.PartnerAdmin],
          title: 'Partnerships',
          urls: [
            { title: 'Admin' },
            { title: 'Partnerships' }
          ],
          videos: [
            { url: 'http://cdn.situsystems.com/learn/situpartnerships/SituPartnerships01_Overview.mp4', title: 'situPARTNERSHIP - Overview' },
            { url: 'http://cdn.situsystems.com/learn/situpartnerships/SituPartnerships02_ViewingPartnerships.mp4', title: 'situPARTNERSHIP - Viewing Partnerships' },
            { url: 'http://cdn.situsystems.com/learn/situpartnerships/SituPartnerships03_RequestingPartnerships.mp4', title: 'situPARTNERSHIP - Requesting Partnerships' },
            { url: 'http://cdn.situsystems.com/learn/situpartnerships/SituPartnerships04_AcceptingPartnerships.mp4', title: 'situPARTNERSHIP - Accepting Partnerships' }
          ]
        }
      },
      {
        path: 'admin/partnerships/new', component: PartnershipRequestComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.PartnerAdmin],
          title: 'Partnerships',
          urls: [
            { title: 'Admin' },
            { title: 'Partnerships', url: '/admin/partnerships' },
            { title: 'Request' }
          ]
        }
      },
      {
        path: 'admin/partnerships/:partnershipId', component: PartnershipComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.PartnerAdmin],
          title: ':companyName',
          urls: [
            { title: 'Admin' },
            { title: 'Partnerships', url: '/admin/partnerships' },
            { title: ':companyName' }
          ]
        }
      },
      {
        path: 'admin/companies', component: CompaniesListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Companies',
          urls: [
            { title: 'Admin' },
            { title: 'Companies' }
          ]
        }
      },
      {
        path: 'admin/report-subscriptions', component: CompanyReportSubscriptionsComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper], title: 'Report Subscriptions',
          urls: [
            { title: 'Admin' },
            { title: 'Report Subscriptions' }
          ]
        }
      },
      {
        path: 'admin/suppliers', component: RequestedSupplierListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Requested Suppliers',
          urls: [
            { title: 'Admin' },
            { title: 'Suppliers' }
          ]
        }
      },
      {
        path: 'admin/companies/add', component: CompaniesAddEditComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
          
          title: 'Add Company',
          urls: [
            { title: 'Companies', url: '/admin/companies' },
            { title: 'Add Company :companyName' }
          ]
        }
      },

      {
        path: 'admin/companies/:companyId', component: CompaniesAddEditComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
          title: ':companyName',
          urls: [
            { title: 'Admin' },
            { title: 'Companies', url: '/admin/companies' },
            { title: ':companyName' }
          ]
        }
      },
      {
        path: 'admin/companies/:companyId/dashboard', component: CompanyComponent, canActivate: [AuthGuard],
        data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          title: ':companyName',
          urls: [
            { title: 'Admin' },
            { title: 'Companies', url: '/admin/companies' },
            { title: ':companyName' }
          ]
        }
      },
      {
        path: 'admin/environments', component: EnvironmentsListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Envrionments',
          urls: [
            { title: 'Admin' },
            { title: 'Environments' },
          ]
        }
      },
      {
        path: 'admin/environments/add-override', component: OverridesAddEditComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee], title: 'Add Override',
          urls: [
            { title: 'Admin' },
            { title: 'Environments' },
            { title: 'Add Override' }
          ]
        }
      },

      // styles
      {
        path: 'admin/styles', component: StyleTreeComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release],
          title: 'Styles', urls: [
            { title: 'Admin' },
            { title: 'Styles' }
          ]
        }
      },
      // {
      //   path: 'admin/style-list', component: StyleListComponent, canActivate: [AuthGuard], data: {
      //     roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee],
      //     title: 'Styles', urls: [
      //       { title: 'Admin' },
      //       { title: 'Styles' }
      //     ]
      //   }
      // },
      {
        path: 'admin/style-template', component: StyleTemplateListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release],
          title: 'Style Templates', urls: [
            { title: 'Admin' },
            { title: 'Style Templates' }
          ]
        }
      },
      {
        path: 'admin/style-template/:id', component: StyleTemplateEditComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release],
          title: ':styleTemplateName', urls: [
            { title: 'Admin' },
            { title: 'Style Templates', url: '/admin/style-template' },
            { title: 'Style Template' }
          ]
        }
      },
      {
        path: 'admin/accessory-bundle', component: AccessoryBundleListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release],
          title: 'Accessory Bundles', urls: [
            { title: 'Admin' },
            { title: 'Accessory Bundles' }
          ]
        }
      },
      {
        path: 'admin/accessory-bundle/add', component: AccessoryBundleAddComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release],
          title: 'New Accessory Bundle', urls: [
            { title: 'Admin' },
            { title: 'Accessory Bundles', url: '/admin/accessory-bundle' },
            { title: 'New' }
          ]
        }
      },
      {
        path: 'admin/accessory-bundle/:id', component: AccessoryBundleEditComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release],
          title: ':accessoryBundleName', urls: [
            { title: 'Admin' },
            { title: 'Accessory Bundles', url: '/admin/accessory-bundle' },
            { title: 'Accessory Bundle' }
          ]
        }
      },

      //admin/ifc-config
      {
        path: 'admin/ifc-config/rooms', component: IfcRoomsConfigComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          
          title: 'Room Configuration', urls: [
            { title: 'Admin' },
            { title: 'IFC Configuration' },
            { title: 'Room' }
          ]
        }
      },
      {
        path: 'admin/ifc-config/building-materials', component: IfcBuildingMaterialsConfigComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          
          title: 'Building Materials Configuration', urls: [
            { title: 'Admin' },
            { title: 'IFC Configuration' },
            { title: 'Building Materials' }
          ]
        }
      },
      {
        path: 'admin/ifc-config/storeys', component: IfcStoreysConfigComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          
          title: 'Storeys Configuration', urls: [
            { title: 'Admin' },
            { title: 'IFC Configuration' },
            { title: 'Storeys' }
          ]
        }
      },
      {
        path: 'admin/ifc-config/furnishings', component: IfcFurnishingsConfigComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          
          title: 'Furnishings Configuration', urls: [
            { title: 'Admin' },
            { title: 'IFC Configuration' },
            { title: 'Furnishings' }
          ]
        }
      },

      // tags
      {
        path: 'admin/tags', component: TagListComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin],
          title: 'Tags', urls: [
            { title: 'Admin' },
            { title: 'Tags' }
          ]
        }
      },

      // file vault
      {
        path: 'admin/file-vault', component: FileVaultBrowserComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.CompanyProductPublisher],
          title: 'File Vault Browser', urls: [
            { title: 'Admin' },
            { title: 'File Vault Browser' }
          ]
        }
      },

      // approve users
      {
        path: 'sign-up/approve-new-user/:userId', component: ApproveNewUserComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.PartnerAdmin, Role.Admin],
          title: 'Approve :userName for :companyName', urls: [
            { title: 'Admin' },
            { title: 'Approve :userName for :companyName' }
          ]
        },
      },
      {
        path: 'sign-up/approve-new-company-user/:userId', component: ApproveNewUserComponent, canActivate: [AuthGuard], data: {
          roles: [Role.SituSystemsAdmin, Role.SituSystemsDeveloper],
          title: 'Approve :userName for :companyName', urls: [
            { title: 'Admin' },
            { title: 'Approve :userName for :companyName' }
          ]
        },
      },
    ]
  },

  {
    // The Partial component behaves the same as the FullComponent, but with the Header and Side Menus removed
    path: '',
    component: PartialComponent,
    children: [
      { path: 'homes/themes/interactive-theme-editor', component: InteractiveThemeEditor },
    ]
  },

  { path: 'situ-view-readonly', component: SituViewReadonlyComponent },
  { path: ':id', component: SituReadonlyFactoryComponent },
  { path: ':id/:to', component: SituReadonlyFactoryComponent },

  { path: 'sign-up', component: LandingPageComponent },
  { path: 'sign-up/start', component: CredentialsComponent },
  { path: 'login', component: CredentialsComponent },
  { path: 'sign-up/company', component: UserDetailsComponent, canActivate: [AuthGuard] },
  { path: 'sign-up/verify-email', component: VerifyEmailComponent },

  //catch all
  { path: '**', redirectTo: '/' }
];
